import { useEffect, useState } from "react";
import axios from "axios";
import { PUBLIC_BASE } from "../lib/api";

const KINDS = [
  { id: "love", emoji: "❤️", label: "Love" },
  { id: "fire", emoji: "🔥", label: "Fire" },
  { id: "wow", emoji: "😍", label: "Wow" },
  { id: "thumbs", emoji: "👍", label: "Like" },
];

/**
 * 1-tap emoji reaction bar shown under each reel on the public share page.
 * Tracks per-browser via localStorage (`reacted:{token}:{kind}`) to avoid
 * trivial multi-tap inflation; nothing fancier — viewer-side fraud isn't
 * worth fighting for these counts.
 */
export default function ReelReactions({ shareToken, brand }) {
  const [counts, setCounts] = useState({ love: 0, fire: 0, wow: 0, thumbs: 0 });
  const [reacted, setReacted] = useState({});

  useEffect(() => {
    if (!shareToken) return;
    axios
      .get(`${PUBLIC_BASE}/api/share/reels/${shareToken}/reactions`)
      .then((r) => setCounts(r.data || {}))
      .catch(() => {});
    const local = {};
    KINDS.forEach((k) => {
      if (localStorage.getItem(`reacted:${shareToken}:${k.id}`)) local[k.id] = true;
    });
    setReacted(local);
  }, [shareToken]);

  const tap = async (kind) => {
    if (reacted[kind]) return;
    setReacted((p) => ({ ...p, [kind]: true }));
    setCounts((p) => ({ ...p, [kind]: (p[kind] || 0) + 1 }));
    localStorage.setItem(`reacted:${shareToken}:${kind}`, "1");
    try {
      await axios.post(`${PUBLIC_BASE}/api/share/reels/${shareToken}/reactions`, { kind });
    } catch {
      // Rollback on failure
      setReacted((p) => ({ ...p, [kind]: false }));
      setCounts((p) => ({ ...p, [kind]: Math.max(0, (p[kind] || 1) - 1) }));
      localStorage.removeItem(`reacted:${shareToken}:${kind}`);
    }
  };

  return (
    <div
      className="mt-4 flex flex-wrap gap-2"
      data-testid="reel-reactions"
    >
      {KINDS.map((k) => {
        const active = !!reacted[k.id];
        const count = counts[k.id] || 0;
        return (
          <button
            key={k.id}
            onClick={() => tap(k.id)}
            disabled={active}
            className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-all ${
              active
                ? "bg-white/10 border-white/30 cursor-default"
                : "bg-white/[0.03] border-white/10 hover:bg-white/[0.08] hover:border-white/20"
            }`}
            style={active && brand ? { borderColor: `${brand}80` } : undefined}
            data-testid={`reel-reaction-${k.id}`}
            title={k.label}
          >
            <span className="text-base leading-none group-hover:scale-125 transition-transform">
              {k.emoji}
            </span>
            <span className="font-mono text-zinc-300">{count}</span>
          </button>
        );
      })}
    </div>
  );
}
